import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, HostListener, inject, NgModule } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxsModule, select } from '@ngxs/store';
import { throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import {
  ChangeForgotPasswordState,
  ChangeForgotPasswordStateChangeForgotPassword,
} from './change-forgot-password.state';
import { Navigate } from '@ngxs/router-plugin';
import { catchError, tap } from 'rxjs/operators';
import {
  AnimateModule,
  BasicPage,
  FormErrorHandlerService,
  GtmClickDirective,
  HeaderComponent,
  InputShowPasswordDirective,
  InputValidatorDirective,
  InvalidControlScrollDirective,
  matchOtherValidator,
  NewsletterComponent,
  PasswordValidator,
  ValidateMessageComponent,
  WwwRouteSlug,
} from '@pro-connect/shared';
import { NgxsFormDirective } from '@ngxs/form-plugin';

@NgModule({
  imports: [NgxsModule.forFeature([ChangeForgotPasswordState])],
})
class ChangeForgotPasswordStateLazyModule {}

export type ChangeForgotPasswordFormType = {
  password: FormControl<string | null>;
  repeatPassword: FormControl<string | null>;
};

@Component({
  templateUrl: './change-forgot-password.component.html',
  styleUrls: ['./change-forgot-password.component.scss'],
  imports: [
    CommonModule,
    ChangeForgotPasswordStateLazyModule,
    InvalidControlScrollDirective,
    NewsletterComponent,
    ReactiveFormsModule,
    NgxsFormDirective,
    AnimateModule,
    InputValidatorDirective,
    NgOptimizedImage,
    ValidateMessageComponent,
    InputShowPasswordDirective,
    RouterLink,
    HeaderComponent,
    GtmClickDirective,
  ],
})
export class ChangeForgotPasswordComponent extends BasicPage {
  public readonly loading = select(ChangeForgotPasswordState.loading);
  public readonly WwwRouteSlug = WwwRouteSlug;
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  public readonly formGroup: FormGroup<ChangeForgotPasswordFormType> = this._formBuilder.group({
    password: new UntypedFormControl(null, [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(255),
      PasswordValidator.number,
      PasswordValidator.upper,
      PasswordValidator.lower,
      PasswordValidator.special,
    ]),
    repeatPassword: new UntypedFormControl(null, [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(255),
      // @ts-ignore
      matchOtherValidator('password'),
    ]),
  });
  private readonly _token: string;

  constructor(
    private readonly _toastrService: ToastrService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _formErrorHandlerService: FormErrorHandlerService,
  ) {
    super();
    this._token = this._activatedRoute.snapshot.params['token'];
  }

  get f() {
    return this.formGroup.controls;
  }

  @HostListener('document:keydown.enter', ['$event'])
  public onEnterKeydownHandler(event: KeyboardEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.onChangeForgotPassword();
  }

  public onChangeForgotPassword(): void {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }
    this._store
      .dispatch(new ChangeForgotPasswordStateChangeForgotPassword({ token: this._token }))
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError((error) => {
          if (error === 'timeout') {
            this._toastrService.error('Błąd przekroczenia limitu czasu żądania', 'Błąd');
          }
          this._formErrorHandlerService.catchFormError(error, this.formGroup);
          if (error?.status === 404) {
            this._toastrService.error('Token wygasł lub jest nieprawidłowy.', 'Błąd');
          } else if (error?.error?.message) {
            this._toastrService.error(error.error.message, 'Błąd');
          } else {
            this._toastrService.error('Coś poszło nie tak.', 'Błąd');
          }
          return throwError(() => error);
        }),
        tap(() => {
          this._toastrService.success('Twoje hasło zostało zmienione.', 'Sukces');
          this._store.dispatch(new Navigate([`/${WwwRouteSlug.LOGIN}`]));
          this.formGroup.reset();
        }),
      )
      .subscribe();
  }
}
